import AutocompleteInput from 'components/organisms/AutocompleteInput/AutocompleteInput'
import { usePaginatedCompanySearch } from 'lib/apollo/hooks'
import { BetterIdCompany, ManualInputFormData, Option } from 'common/types'
import { TRepSignUpFormData } from './AddRepModal/RepSignupForm/repSignupForm.types'
import { useState, useEffect, ChangeEvent } from 'react'
import { useApolloClients } from 'lib/apollo/apolloProvider'

interface CompanySearchBarProps {
  handleChangeFormData: (option: Option) => void
  manualAssetInputFormData?: ManualInputFormData | TRepSignUpFormData
  variant?: 'standard' | 'outlined'
  disabled?: boolean
  required?: boolean
  error?: boolean
  helperText?: string
  mandatory?: boolean
  label?: string
  placeholder?: string
  propValue?: Option
  clearOnSelect?: boolean
  companiesIds?: number[]
}

export const CompanySearchBar: React.FC<CompanySearchBarProps> = ({
  handleChangeFormData,
  manualAssetInputFormData,
  variant,
  disabled = false,
  required = true,
  error,
  helperText,
  mandatory = false,
  label = 'Search Hospital',
  placeholder = 'Search Hospital',
  propValue,
  clearOnSelect = false,
  companiesIds,
}) => {
  const value = propValue?.name || manualAssetInputFormData?.companyName
  const [companyNameInput, setCompanyNameInput] = useState<string | undefined>(
    value
  )
  const { servicesClient } = useApolloClients()

  const [
    getCompanies,
    { loading: isLoadingCompanies, data: rawCompaniesData },
  ] = usePaginatedCompanySearch({ client: servicesClient })

  const companiesList =
    (rawCompaniesData?.companySearchPaginated.companies?.map(
      (company: BetterIdCompany) => ({
        name: company.parentCompanyName
          ? `${company.parentCompanyName} [${company.name}]`
          : company.name,
        id: company.id,
      })
    ) as Option[]) ?? []

  const handleCompanyNameInputChange = (
    e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    setCompanyNameInput(e.target.value)
  }

  useEffect(() => {
    setCompanyNameInput(
      manualAssetInputFormData?.companyName || propValue?.name || ''
    )
  }, [manualAssetInputFormData?.companyName, propValue?.name])

  useEffect(() => {
    if (companyNameInput || companiesIds?.length) {
      getCompanies({
        variables: {
          data: {
            name: companyNameInput || undefined,
            limit: 20,
            companiesIds:
              companiesIds?.length && !companyNameInput
                ? companiesIds
                : undefined,
          },
        },
      })
    }
  }, [companyNameInput, companiesIds])

  return (
    <AutocompleteInput
      id="companyName"
      label={mandatory ? `${label}*` : label}
      name="companyName"
      variant={variant}
      value={value}
      handleChangeFormData={(e) => {
        if (e.target.value === '') return
        handleChangeFormData(e.target.value as Option)
      }}
      onTextInputChange={(e) =>
        handleCompanyNameInputChange(
          e as ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
        )
      }
      options={companiesList}
      autoSelect={false}
      isLoading={isLoadingCompanies}
      required={required}
      sx={{ my: 0 }}
      disabled={disabled}
      error={error}
      helperText={helperText}
      placeholder={placeholder}
      shrink={!!companyNameInput || !!companiesIds?.length}
      clearOnSelect={clearOnSelect}
      isOptionEqualToValue={(option, value) => {
        if (typeof option === 'object' && typeof value === 'object') {
          return option.id === value.id
        }
        return false
      }}
    />
  )
}
